import React, { useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { BookingData } from "../../../types/bookings";
import VenueAutocomplete from "./components/VenueAutoComplete";
import VenueDialog from "./components/AddVenueDialog";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import { Venue } from "../../../types/venue";
import CustomTextField from "../../../components/common/Textfield";
import { LocationOn } from "@mui/icons-material";

interface VenueDetailsProps {
  serviceType: string;
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
}

const SiteDetails: React.FC<VenueDetailsProps> = ({
  serviceType,
  bookingData,
  setBookingData,
}) => {
  const [venueOptions, setVenueOptions] = useState<Venue[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [newVenue, setNewVenue] = useState<Venue>({
    name: "",
    address_one: "",
    address_two: "",
    suburb: "",
    city: "Harare",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [venueLoading, setVenueLoading] = useState<boolean>(false);

  const [venueSearchTerm, setVenueSearchTerm] = useState("");

  const reduxTheme = useSelector((state: RootState) => state.theme);

  // Fetch initial selected venues
  useEffect(() => {
    const loadSelectedVenue = async () => {
      const venueId = bookingData.venue_ids?.[0];

      if (venueId && !selectedVenue) {
        setVenueLoading(true);
        try {
          const response = await apiService.get<Venue>(
            `/api/venues/venue/${venueId}/`
          );
          setSelectedVenue(response.data);
        } catch (err: any) {
          console.error("Failed to fetch selected venue:", err);
          toast.error("Failed to load selected venue. Please try again.", {
            position: "top-right",
            autoClose: 5000,
            theme: "light",
          });
        } finally {
          setVenueLoading(false);
        }
      }
    };

    loadSelectedVenue();
  }, [bookingData.venue_ids, selectedVenue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (venueSearchTerm.length >= 3) {
        fetchVenues(venueSearchTerm);
      } else {
        setVenueOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [venueSearchTerm]);

  const handleNewVenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewVenue((prev) => ({ ...prev, [name]: value }));
  };

  const fetchVenues = async (search: string) => {
    setVenueLoading(true);
    try {
      const response = await apiService.get<{ results: Venue[] }>(
        `/api/venues/venue/?search=${search}`
      );
      setVenueOptions(response.data.results || []);
    } catch (err: any) {
      console.error("Failed to fetch venues:", err);
      toast.error("Failed to load venues. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setVenueLoading(false);
    }
  };

  const handleVenueChange = (
    event: React.SyntheticEvent,
    newValue: Venue | null
  ) => {
    setSelectedVenue(newValue);

    if (!newValue) {
      setBookingData((prevData) => ({
        ...prevData,
        venue_ids: [],
        name: "",
        address_one: "",
        address_two: "",
        city: "",
        suburb: "",
        areAddressFieldsDisabled: false,
      }));
      return;
    }

    const {
      id,
      name = "",
      address_one = "",
      address_two = "",
      city = "",
      suburb = "",
    } = newValue;

    setBookingData((prevData) => ({
      ...prevData,
      venue_ids: [String(id)],
      name,
      address_one,
      address_two,
      city,
      suburb,
      areAddressFieldsDisabled: true,
    }));
  };

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => {
    setOpen(false);
    setNewVenue({
      name: "",
      address_one: "",
      address_two: "",
      suburb: "",
      city: "",
    });
  };

  const handleAddVenue = async () => {
    setLoading(true);
    try {
      const response = await apiService.post<Venue>(
        "/api/venues/venue/",
        newVenue
      );

      setVenueOptions((prev) => [...prev, response.data]);

      setSelectedVenue(response.data);

      setBookingData((prevData) => ({
        ...prevData,
        name: response.data.name || "",
        venue_ids: [String(response.data.id)],
        address_one: response.data.address_one || "",
        address_two: response.data.address_two || "",
        city: response.data.city || "",
        suburb: response.data.suburb || "",
        areAddressFieldsDisabled: true,
      }));

      toast.success("Venue added successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      handleCloseDialog();
    } catch (err: any) {
      console.error("Failed to add venue:", err);
      let errorMessage = "Failed to add venue. Please try again.";
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <LocationOn sx={{ mr: 1, color: "primary.main", ml: 2 }} />
        <Typography variant="h6" fontWeight={600}>
          Venue Details
        </Typography>
      </Box>
      <Grid container spacing={2} mt={1}>
        <Grid size={{ xs: 12, sm: 3 }}>
          {" "}
          <VenueAutocomplete
            value={selectedVenue}
            onChange={handleVenueChange}
            onInputChange={(e, value) => setVenueSearchTerm(value)}
            onAddVenueClick={handleOpenDialog}
            options={venueOptions}
            loading={venueLoading}
            required={serviceType === "CONFERENCE"}
            serviceType={serviceType}
            theme={reduxTheme}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <CustomTextField
            label="Address One"
            fullWidth
            value={bookingData.address_one || ""}
            disabled={bookingData.areAddressFieldsDisabled}
            onChange={(e) =>
              setBookingData((prev) => ({
                ...prev,
                address_one: e.target.value,
              }))
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <CustomTextField
            label="Address Two"
            fullWidth
            value={bookingData.address_two || ""}
            disabled={bookingData.areAddressFieldsDisabled}
            onChange={(e) =>
              setBookingData((prev) => ({
                ...prev,
                address_two: e.target.value,
              }))
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <CustomTextField
            label="Suburb"
            fullWidth
            value={bookingData.suburb || ""}
            disabled={bookingData.areAddressFieldsDisabled}
            onChange={(e) =>
              setBookingData((prev) => ({
                ...prev,
                suburb: e.target.value,
              }))
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <CustomTextField
            label="City"
            fullWidth
            value={bookingData.city || ""}
            disabled={bookingData.areAddressFieldsDisabled}
            onChange={(e) =>
              setBookingData((prev) => ({
                ...prev,
                city: e.target.value,
              }))
            }
          />
        </Grid>
      </Grid>
      <VenueDialog
        open={open}
        onClose={handleCloseDialog}
        onSubmit={handleAddVenue}
        loading={loading}
        newVenue={newVenue}
        onChange={handleNewVenueChange}
        cities={reduxTheme.cities}
        theme={reduxTheme}
      />
    </Box>
  );
};

export default SiteDetails;
